import { useQuery, gql, NetworkStatus } from "@apollo/client";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  SyncConfig,
  SyncTarget,
  UseSubgraphSyncResult,
  SubgraphStatus,
  Block,
} from "../types";

const GET_SUBGRAPH_STATUS = gql`
  query GetSubgraphStatus {
    _meta {
      block {
        number
        timestamp
      }
      hasIndexingErrors
      deployment
    }
  }
`;

const DEFAULT_CONFIG: Required<SyncConfig> = {
  pollInterval: 2000,
  timeout: 120000,
  maxRetries: 3,
  onTimeout: () => {},
  onError: () => {},
  onSyncComplete: () => {},
};

/**
 * Hook for tracking subgraph synchronization progress to a target block or timestamp.
 *
 * @param target - The sync target (block number and/or timestamp)
 * @param config - Optional configuration for polling, timeout, retries, and callbacks
 *
 * @returns Sync state including indexing status, progress, current block, and control functions
 *
 * @example
 * ```tsx
 * const syncState = useSubgraphSync(
 *   { blockNumber: 1000000 },
 *   {
 *     pollInterval: 2000,
 *     timeout: 60000,
 *     onSyncComplete: () => console.log('Synced!'),
 *   }
 * );
 *
 * if (syncState.isIndexing) {
 *   return <div>Progress: {syncState.progress}%</div>;
 * }
 * ```
 */
export const useSubgraphSync = (
  target: SyncTarget,
  config: SyncConfig = {}
): UseSubgraphSyncResult => {
  const mergedConfig = useRef({ ...DEFAULT_CONFIG, ...config });

  // Update mergedConfig ref when config changes
  useEffect(() => {
    mergedConfig.current = { ...DEFAULT_CONFIG, ...config };
  }, [config]);

  const [state, setState] = useState<
    Omit<UseSubgraphSyncResult, "refetch" | "reset">
  >({
    isIndexing: false,
    isSynced: false,
    isTimeout: false,
    isError: false,
    currentBlock: null,
    targetBlock: null,
    progress: 0,
    error: null,
    retries: 0,
  });

  const timeoutRef = useRef<number>();
  const startTimeRef = useRef<number>(Date.now());
  const retryCountRef = useRef<number>(0);

  const { data, loading, error, networkStatus, refetch } = useQuery<{
    _meta: SubgraphStatus;
  }>(GET_SUBGRAPH_STATUS, {
    pollInterval: mergedConfig.current.pollInterval,
    notifyOnNetworkStatusChange: true,
    skip: !target.blockNumber && !target.timestamp,
    onError: (error) => {
      if (retryCountRef.current < mergedConfig.current.maxRetries) {
        retryCountRef.current += 1;
        setState((prev) => ({ ...prev, retries: retryCountRef.current }));
      } else {
        setState((prev) => ({
          ...prev,
          isError: true,
          error: error.message,
        }));
        mergedConfig.current.onError?.(error);
      }
    },
  });

  const calculateProgress = useCallback(
    (currentBlock: Block | null): number => {
      if (!currentBlock || !target.blockNumber) return 0;
      if (currentBlock.number >= target.blockNumber) return 100;

      // Cap at 95% until fully synced to avoid showing 100% prematurely
      return Math.min(95, (currentBlock.number / target.blockNumber) * 100);
    },
    [target.blockNumber]
  );

  const reset = useCallback(() => {
    startTimeRef.current = Date.now();
    retryCountRef.current = 0;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setState({
      isIndexing: !!(target.blockNumber || target.timestamp),
      isSynced: false,
      isTimeout: false,
      isError: false,
      currentBlock: null,
      targetBlock: target.blockNumber
        ? {
            number: target.blockNumber,
            timestamp: target.timestamp || 0,
          }
        : null,
      progress: 0,
      error: null,
      retries: 0,
    });
  }, [target.blockNumber, target.timestamp]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    retryCountRef.current = 0;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setState({
      isIndexing: !!(target.blockNumber || target.timestamp),
      isSynced: false,
      isTimeout: false,
      isError: false,
      currentBlock: null,
      targetBlock: target.blockNumber
        ? {
            number: target.blockNumber,
            timestamp: target.timestamp || 0,
          }
        : null,
      progress: 0,
      error: null,
      retries: 0,
    });

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [target.blockNumber, target.timestamp]);

  useEffect(() => {
    // Early exit if no target specified - already synced
    if (!target.blockNumber && !target.timestamp) {
      setState((prev) => ({ ...prev, isIndexing: false, isSynced: true }));
      return;
    }

    // Check if timeout has been exceeded
    if (Date.now() - startTimeRef.current > mergedConfig.current.timeout) {
      setState((prev) => ({
        ...prev,
        isIndexing: false,
        isTimeout: true,
        error: "Subgraph sync timeout",
      }));
      mergedConfig.current.onTimeout?.();
      return;
    }

    const currentBlock = data?._meta?.block;
    if (!currentBlock) return;

    const progress = calculateProgress(currentBlock);

    // Check if we've reached both block and timestamp targets
    const hasReachedTargetBlock = target.blockNumber
      ? currentBlock.number >= target.blockNumber
      : true;
    const hasReachedTargetTimestamp = target.timestamp
      ? currentBlock.timestamp >= target.timestamp
      : true;

    const isSynced = hasReachedTargetBlock && hasReachedTargetTimestamp;

    setState((prev) => ({
      ...prev,
      isIndexing: !isSynced,
      isSynced,
      currentBlock,
      progress: isSynced ? 100 : progress,
      error: data?._meta?.hasIndexingErrors
        ? "Subgraph has indexing errors"
        : null,
      isError: !!data?._meta?.hasIndexingErrors,
    }));

    if (isSynced) {
      mergedConfig.current.onSyncComplete?.(data._meta);
    }
  }, [data, target.blockNumber, target.timestamp, calculateProgress]);

  return {
    ...state,
    refetch,
    reset,
  };
};
